// frontend\src\components\analytics\Analytics.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useAnalytics from '../../hooks/useAnalytics';
import AnalyticsFilters from './AnalyticsFilters';
import AnalyticsLineChart from './AnalyticsLineChart';
import AnalyticsPieChart from './AnalyticsPieChart';
import AnalyticsCustomerTypeChart from './AnalyticsCustomerTypeChart';
import AnalyticsTagChart from './AnalyticsTagChart';
import AnalyticsReports from './AnalyticsReports';
import AnalyticsKpiCards from './AnalyticsKpiCards';
import AnalyticsStockAdditions from './AnalyticsStockAdditions';
import AnalyticsInventoryDashboard from './AnalyticsInventoryDashboard';
import AnalyticsInventoryValue from './AnalyticsInventoryValue';
import AnalyticsInventoryValueHistory from './AnalyticsInventoryValueHistory';
import AnalyticsOverstock from './AnalyticsOverstock';
import InventorySearchFilter from './InventorySearchFilter';
import '../../analytics.css';
import LoadingSpinner from '../LoadingSpinner';
import { buildFilterSummary } from '../../utils/filterSummary';

interface AnalyticsProps {
  isAdmin: boolean;
}

const Analytics: React.FC<AnalyticsProps> = ({ isAdmin }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'sales' | 'inventory'>('sales');

  // Inventory specific state
  const [inventorySku, setInventorySku] = useState<string>('MPP-TM3-RUCA');
  const [inventoryStartDate, setInventoryStartDate] = useState<string>('');
  const [inventoryEndDate, setInventoryEndDate] = useState<string>('');

  // Inventory date picker
  const [showInventoryDatePicker, setShowInventoryDatePicker] = useState<boolean>(false);
  const [tempInventoryStartDate, setTempInventoryStartDate] = useState<string>('');
  const [tempInventoryEndDate, setTempInventoryEndDate] = useState<string>('');

  const {
    analyticsData,
    popularProducts,
    topCustomers,
    loading,
    loadingProducts,
    loadingCustomers,
    error,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    compareMode,
    setCompareMode,
    comparePeriods,
    setComparePeriods,
    timeBase,
    setTimeBase,
    metric,
    setMetric,
    search,
    setSearch,
    compareSearch,
    setCompareSearch,
    selectedCountry,
    setSelectedCountry,
    selectedRegion,
    setSelectedRegion,
    selectedCustomerType,
    setSelectedCustomerType,
    selectedCompareCustomerType,
    setSelectedCompareCustomerType,
    selectedCustomerName,
    setSelectedCustomerName,
    selectedTag,
    setSelectedTag,
    selectedCompareTag,
    setSelectedCompareTag,
    selectedSupplier,
    setSelectedSupplier,
    selectedCompareSupplier,
    setSelectedCompareSupplier,
    fetchPopularProducts,
    fetchTopCustomers,
    setPopularProducts,
    setTopCustomers,
  } = useAnalytics();

  useEffect(() => {
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1); // January 1st of current year
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    if (!startDate && !endDate) {
      setStartDate(formatDate(firstDayOfYear));
      setEndDate(formatDate(today));
      setCompareMode('previousYear'); // Set comparison to previous year
      setTimeBase('monthly'); // Set default time base to monthly
      setMetric('sales');
    }
    // Initialize inventory dates
    if (!inventoryStartDate && !inventoryEndDate) {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const start = formatDate(threeMonthsAgo);
      const end = formatDate(today);
      setInventoryStartDate(start);
      setInventoryEndDate(end);
      setTempInventoryStartDate(start);
      setTempInventoryEndDate(end);
    }
  }, [startDate, endDate, setStartDate, setEndDate, setCompareMode, setTimeBase, setMetric, inventoryStartDate, inventoryEndDate, setInventoryStartDate, setInventoryEndDate]);

  const formatDateRange = (start: string, end: string) => {
    if (!start || !end) return '';
    const parseDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const startFormatted = parseDate(start).toLocaleDateString('en-US', options);
    const endFormatted = parseDate(end).toLocaleDateString('en-US', options);
    return `${startFormatted} - ${endFormatted}`;
  };

  const handleInventoryMonthToDate = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setTempInventoryStartDate(startOfMonth.toISOString().split('T')[0]);
    setTempInventoryEndDate(today.toISOString().split('T')[0]);
  };

  const handleInventoryYearToDate = () => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    setTempInventoryStartDate(startOfYear.toISOString().split('T')[0]);
    setTempInventoryEndDate(today.toISOString().split('T')[0]);
  };

  const applyInventoryDateFilter = () => {
    setInventoryStartDate(tempInventoryStartDate);
    setInventoryEndDate(tempInventoryEndDate);
    setShowInventoryDatePicker(false);
  };

  const cancelInventoryDateFilter = () => {
    setTempInventoryStartDate(inventoryStartDate);
    setTempInventoryEndDate(inventoryEndDate);
    setShowInventoryDatePicker(false);
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (error) return <div className="analytics-container">Error: {error}</div>;
  if (loading) return <LoadingSpinner />;

  const isDataReady = analyticsData.data?.length > 0 || analyticsData.geoData?.length > 0;

  // Selecting a tag from the pie chart only sets the tag filter, but a tag picked in the filter
  // box also lives in the search string as a chip. Clearing has to undo both, or the tag lingers
  // there as a plain product/customer search term.
  const clearTagFilter = () => {
    const tags = (selectedTag || '').split(',').filter(Boolean);
    setSearch((prev) =>
      prev
        .split(',')
        .filter((value) => value && !tags.includes(value))
        .join(',')
    );
    setSelectedTag(null);
  };

  const filterSummary = buildFilterSummary({
    metric,
    search,
    tags: selectedTag,
    supplier: selectedSupplier,
    customerType: selectedCustomerType,
    customerName: selectedCustomerName,
    region: selectedRegion,
    country: selectedCountry,
  });

  return (
    <div className="analytics-container">
      <div className="analytics-tabs">
        <button
          className={`analytics-tab ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => setActiveTab('sales')}
        >
          Sales
        </button>
        <button
          className={`analytics-tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory
        </button>
      </div>
      <p className="last-updated">
        {activeTab === 'sales'
          ? formatDateRange(startDate, endDate)
          : formatDateRange(inventoryStartDate, inventoryEndDate)
        }
      </p>
      {activeTab === 'sales' && (
        <>
          <AnalyticsFilters
            search={search}
            setSearch={setSearch}
            compareSearch={compareSearch}
            setCompareSearch={setCompareSearch}
            timeBase={timeBase}
            setTimeBase={setTimeBase}
            metric={metric}
            setMetric={setMetric}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            compareMode={compareMode}
            setCompareMode={setCompareMode}
            comparePeriods={comparePeriods}
            setComparePeriods={setComparePeriods}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            selectedCompareTag={selectedCompareTag}
            setSelectedCompareTag={setSelectedCompareTag}
            selectedCustomerType={selectedCustomerType}
            setSelectedCustomerType={setSelectedCustomerType}
            selectedCompareCustomerType={selectedCompareCustomerType}
            setSelectedCompareCustomerType={setSelectedCompareCustomerType}
            selectedSupplier={selectedSupplier}
            setSelectedSupplier={setSelectedSupplier}
            selectedCompareSupplier={selectedCompareSupplier}
            setSelectedCompareSupplier={setSelectedCompareSupplier}
          />
          {isDataReady && (
            <>
              <AnalyticsKpiCards
                data={analyticsData.data}
                compareData={analyticsData.compareData}
                revenueBasis={analyticsData.revenueBasis}
              />
              <AnalyticsLineChart
                data={analyticsData.data}
                compareData={analyticsData.compareData}
                compareSeries={analyticsData.compareSeries}
                metric={metric}
                totalMetric={analyticsData.totalMetric}
                percentageChange={analyticsData.percentageChange}
                startDate={startDate}
                endDate={endDate}
                compareMode={compareMode}
                compareFilterLabel={analyticsData.compareFilterLabel}
                revenueBasis={analyticsData.revenueBasis}
                filterSummary={filterSummary}
              />
              <div className="analytics-pie-charts">
                <AnalyticsPieChart
                  geoData={analyticsData.geoData}
                  selectedRegion={selectedRegion}
                  selectedCountry={selectedCountry}
                  setSelectedRegion={setSelectedRegion}
                  setSelectedCountry={setSelectedCountry}
                  metric={metric}
                />
                <AnalyticsCustomerTypeChart
                  customerTypeData={analyticsData.customerTypeData}
                  selectedCustomerType={selectedCustomerType}
                  setSelectedCustomerType={setSelectedCustomerType}
                  selectedCustomerName={selectedCustomerName}
                  setSelectedCustomerName={setSelectedCustomerName}
                  metric={metric}
                />
                <AnalyticsTagChart
                  tagData={analyticsData.tagData}
                  tagLevel={analyticsData.tagLevel || 'group'}
                  selectedTag={selectedTag}
                  onSelectTag={(tagName) => setSelectedTag(tagName)}
                  onClearTag={clearTagFilter}
                  metric={metric}
                />
              </div>
              <AnalyticsReports
                popularProducts={popularProducts}
                topCustomers={topCustomers}
                loadingProducts={loadingProducts}
                loadingCustomers={loadingCustomers}
                fetchPopularProducts={fetchPopularProducts}
                fetchTopCustomers={fetchTopCustomers}
                setPopularProducts={setPopularProducts}
                setTopCustomers={setTopCustomers}
                startDate={startDate}
                endDate={endDate}
                compareMode={compareMode}
                search={search}
                selectedTag={selectedTag}
                selectedCustomerName={selectedCustomerName}
              />
            </>
          )}
        </>
      )}
      {activeTab === 'inventory' && (
        <>
          <div className="filter-container" style={{ flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="analytics-action-button"
                  onClick={() => {
                    setTempInventoryStartDate(inventoryStartDate);
                    setTempInventoryEndDate(inventoryEndDate);
                    setShowInventoryDatePicker(!showInventoryDatePicker);
                  }}
                >
                  Select Dates
                </button>
                {showInventoryDatePicker && (
                  <div className="analytics-date-popover">
                    <input
                      type="date"
                      value={tempInventoryStartDate}
                      onChange={(e) => setTempInventoryStartDate(e.target.value)}
                      className="date-input"
                    />
                    <input
                      type="date"
                      value={tempInventoryEndDate}
                      onChange={(e) => setTempInventoryEndDate(e.target.value)}
                      className="date-input"
                    />
                    <div className="analytics-date-popover-presets">
                      <button className="analytics-action-button" onClick={handleInventoryMonthToDate}>
                        Month To Date
                      </button>
                      <button className="analytics-action-button" onClick={handleInventoryYearToDate}>
                        Year To Date
                      </button>
                    </div>
                    <div className="analytics-date-popover-actions">
                      <button
                        className="analytics-action-button secondary"
                        onClick={cancelInventoryDateFilter}
                      >
                        Cancel
                      </button>
                      <button
                        className="analytics-action-button"
                        onClick={applyInventoryDateFilter}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <InventorySearchFilter
                search={inventorySku}
                setSearch={setInventorySku}
              />
            </div>
          </div>
          <div className="analytics-section-heading">
            <h2>Selected Product{inventorySku.includes(',') ? 's' : ''}</h2>
            <span className="analytics-section-sub">
              Metrics for the product{inventorySku.includes(',') ? 's' : ''} chosen above, over the selected date range
            </span>
          </div>
          {inventoryStartDate && inventoryEndDate && (
            <AnalyticsInventoryDashboard
              sku={inventorySku}
              startDate={inventoryStartDate}
              endDate={inventoryEndDate}
            />
          )}
          <AnalyticsStockAdditions
            startDate={inventoryStartDate}
            endDate={inventoryEndDate}
            search={inventorySku}
            supplier={selectedSupplier || undefined} // Pass selectedSupplier to the component
          />

          <div className="analytics-section-heading">
            <h2>All Products</h2>
            <span className="analytics-section-sub">
              Company-wide inventory across every product, independent of the selection above
            </span>
          </div>
          {inventoryStartDate && inventoryEndDate && (
            <AnalyticsInventoryValueHistory
              startDate={inventoryStartDate}
              endDate={inventoryEndDate}
            />
          )}
          <AnalyticsInventoryValue />
          <AnalyticsOverstock />
        </>
      )}
    </div>
  );
};

export default Analytics;